import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  ShieldCheckIcon,
  BuildingOffice2Icon,
  UserIcon,
  AdjustmentsHorizontalIcon,
  UsersIcon,
  SparklesIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { onboardingApi, settingsApi } from '../utils/api';

/**
 * Post-payment onboarding wizard.
 *
 * Steps:
 *   0. Welcome
 *   1. Company details (incl. Gas Safe Registration No. – business, 1–6 digits)
 *   2. Your profile (incl. Gas Safe Licence No. – engineer, 7 digits)
 *   3. Quote policy (Company plan only; Solo skips automatically)
 *   4. Invite team (Company plan only; skippable)
 *   5. Done
 *
 * Data is persisted step-by-step to the backend so the wizard is safe to
 * resume later (e.g. after closing the browser tab).
 */
type PolicyModel = 'THRESHOLD' | 'ALWAYS_ADMIN' | 'TRUST';

export default function Onboarding() {
  const { user, refreshUser, loading } = useAuth();
  const navigate = useNavigate();

  const [statusData, setStatusData] = useState<any>(null);
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- form state ---
  const [company, setCompany] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    gasRegistrationNumber: '',
    vatRate: 20,
  });
  const [profile, setProfile] = useState({
    name: '',
    phone: '',
    gasSafeNumber: '',
    basePostcode: '',
    serviceRadiusMiles: 25,
    qualifications: [] as string[],
  });
  const [policy, setPolicy] = useState<{
    model: PolicyModel;
    thresholdAmount: number;
    maxDiscountPct: number;
    validDays: number;
    requireCustomerSignature: boolean;
    allowCustomItems: boolean;
    notifyAdminsOnSubmit: boolean;
    autoConvertOnAcceptance: boolean;
  }>({
    model: 'THRESHOLD',
    thresholdAmount: 500,
    maxDiscountPct: 10,
    validDays: 14,
    requireCustomerSignature: true,
    allowCustomItems: true,
    notifyAdminsOnSubmit: true,
    autoConvertOnAcceptance: true,
  });
  const [invites, setInvites] = useState<string[]>(['']);

  const isCompanyPlan = user?.plan === 'COMPANY';

  // Steps change depending on plan
  const steps = useMemo(
    () =>
      isCompanyPlan
        ? ['welcome', 'company', 'profile', 'policy', 'invite', 'done']
        : ['welcome', 'company', 'profile', 'done'],
    [isCompanyPlan],
  );

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (user.onboardingStatus === 'COMPLETED') {
      navigate(user.role === 'SOLO' ? '/solo-dashboard' : '/dashboard', { replace: true });
      return;
    }
    (async () => {
      try {
        const resp = await onboardingApi.status();
        setStatusData(resp.data);
        const p = resp.data?.prefill || {};
        if (p.company) {
          setCompany((c) => ({
            ...c,
            name: p.company.name || '',
            phone: p.company.phone || '',
            email: p.company.email || user.email,
            address: p.company.address || '',
            gasRegistrationNumber: p.company.gasRegistrationNumber || '',
            vatRate: typeof p.company.vatRate === 'number' ? p.company.vatRate : 20,
          }));
        }
        if (p.admin) {
          setProfile((pr) => ({
            ...pr,
            name: p.admin.name || '',
            phone: p.admin.phone || '',
            gasSafeNumber: p.admin.gasSafeNumber || '',
            basePostcode: p.admin.basePostcode || '',
            serviceRadiusMiles: p.admin.serviceRadiusMiles ?? 25,
            qualifications: Array.isArray(p.admin.qualifications) ? p.admin.qualifications : [],
          }));
        }
        if (p.quotePolicy) {
          setPolicy((q) => ({
            ...q,
            model: p.quotePolicy.model || 'THRESHOLD',
            thresholdAmount: p.quotePolicy.thresholdAmount ?? 500,
            maxDiscountPct: p.quotePolicy.maxDiscountPct ?? 10,
            validDays: p.quotePolicy.validDays ?? 14,
            requireCustomerSignature: !!p.quotePolicy.requireCustomerSignature,
            allowCustomItems: !!p.quotePolicy.allowCustomItems,
            notifyAdminsOnSubmit: !!p.quotePolicy.notifyAdminsOnSubmit,
            autoConvertOnAcceptance: !!p.quotePolicy.autoConvertOnAcceptance,
          }));
        }
        // Resume on the last completed step
        const lastStep = Math.max(0, Math.min(resp.data?.step || 0, steps.length - 1));
        setStepIndex(lastStep || 0);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Could not load onboarding status.');
      }
    })();
  }, [loading, user, navigate, steps.length]);

  const go = (delta: number) =>
    setStepIndex((s) => Math.max(0, Math.min(steps.length - 1, s + delta)));

  // ---------------- step handlers ----------------

  const saveCompany = async () => {
    setError(null);
    if (company.gasRegistrationNumber && !/^\d{1,6}$/.test(company.gasRegistrationNumber)) {
      setError('Gas Safe Registration No. is the 1–6 digit business number.');
      return;
    }
    setSubmitting(true);
    try {
      await onboardingApi.saveCompany({
        name: company.name,
        phone: company.phone,
        email: company.email,
        address: company.address,
        gasRegistrationNumber: company.gasRegistrationNumber,
        vatRate: company.vatRate,
      });
      go(1);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not save company details.');
    } finally {
      setSubmitting(false);
    }
  };

  const saveProfile = async () => {
    setError(null);
    if (profile.gasSafeNumber && !/^\d{7}$/.test(profile.gasSafeNumber)) {
      setError('Gas Safe Licence No. is the 7-digit ID card number.');
      return;
    }
    setSubmitting(true);
    try {
      await onboardingApi.saveProfile({
        name: profile.name,
        phone: profile.phone,
        gasSafeNumber: profile.gasSafeNumber,
        basePostcode: profile.basePostcode,
        serviceRadiusMiles: profile.serviceRadiusMiles,
        qualifications: profile.qualifications,
      });
      go(1);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not save profile.');
    } finally {
      setSubmitting(false);
    }
  };

  const savePolicy = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await onboardingApi.saveQuotePolicy(policy);
      go(1);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not save quote policy.');
    } finally {
      setSubmitting(false);
    }
  };

  const sendInvites = async () => {
    setError(null);
    setSubmitting(true);
    const emails = invites.map((e) => e.trim()).filter(Boolean);
    try {
      for (const email of emails) {
        try {
          await settingsApi.inviteMember({ email, role: 'ENGINEER' });
        } catch (e: any) {
          console.warn('Invite failed for', email, e?.response?.data?.message || e?.message);
        }
      }
      go(1);
    } finally {
      setSubmitting(false);
    }
  };

  const finish = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await onboardingApi.complete();
      await refreshUser();
      navigate(user?.role === 'SOLO' ? '/solo-dashboard' : '/dashboard', { replace: true });
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not complete onboarding.');
      setSubmitting(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500" />
      </div>
    );
  }

  const stepKey = steps[stepIndex];
  const stepIcon: Record<string, React.ReactNode> = {
    welcome: <SparklesIcon className="w-5 h-5" />,
    company: <BuildingOffice2Icon className="w-5 h-5" />,
    profile: <UserIcon className="w-5 h-5" />,
    policy: <AdjustmentsHorizontalIcon className="w-5 h-5" />,
    invite: <UsersIcon className="w-5 h-5" />,
    done: <CheckCircleIcon className="w-5 h-5" />,
  };
  const stepLabel: Record<string, string> = {
    welcome: 'Welcome',
    company: 'Company details',
    profile: 'Your profile',
    policy: 'Quote policy',
    invite: 'Invite team',
    done: 'All done',
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex lg:w-72 flex-col border-r border-slate-200 bg-white px-6 py-8">
        <div className="flex items-center gap-2 mb-10">
          <span className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
            <ShieldCheckIcon className="w-5 h-5 text-white" />
          </span>
          <div>
            <div className="font-semibold tracking-tight text-slate-900">Certs</div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400">
              {isCompanyPlan ? 'Company setup' : 'Solo setup'}
            </div>
          </div>
        </div>

        <ol className="space-y-1">
          {steps.map((s, idx) => (
            <li key={s}>
              <button
                onClick={() => setStepIndex(idx)}
                disabled={idx > stepIndex + 1}
                className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition ${
                  idx === stepIndex
                    ? 'bg-slate-900 text-white'
                    : idx < stepIndex
                    ? 'text-slate-600 hover:bg-slate-100'
                    : 'text-slate-400'
                }`}
              >
                <span className="w-6 h-6 rounded-full border flex items-center justify-center">
                  {idx < stepIndex ? (
                    <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <span className="text-xs">{idx + 1}</span>
                  )}
                </span>
                <span className="flex items-center gap-2">
                  {stepIcon[s]} {stepLabel[s]}
                </span>
              </button>
            </li>
          ))}
        </ol>
      </aside>

      {/* Main */}
      <main className="flex-1 px-6 py-10 lg:px-12 overflow-y-auto">
        <div className="max-w-2xl mx-auto">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg">
              {error}
            </div>
          )}

          {stepKey === 'welcome' && (
            <Panel
              title={`Welcome${user.name ? `, ${user.name.split(' ')[0]}` : ''}`}
              subtitle="Let's get your account ready. This only takes a few minutes and you can come back later to tweak anything."
            >
              <ul className="space-y-3 text-sm text-slate-700">
                <PanelBullet>Add your business details (so certificates & invoices look right)</PanelBullet>
                <PanelBullet>Capture your Gas Safe Registration No. & Licence No.</PanelBullet>
                {isCompanyPlan && <PanelBullet>Choose how quotes need admin approval</PanelBullet>}
                {isCompanyPlan && <PanelBullet>(Optional) Invite your engineers</PanelBullet>}
              </ul>
              <PanelActions>
                <button className="btn-primary" onClick={() => go(1)}>
                  Get started
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              </PanelActions>
            </Panel>
          )}

          {stepKey === 'company' && (
            <Panel
              title="Company details"
              subtitle="This shows on every certificate and invoice we produce."
            >
              <div className="space-y-4">
                <Field label="Business name">
                  <input
                    className="input"
                    value={company.name}
                    onChange={(e) => setCompany({ ...company, name: e.target.value })}
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Business phone">
                    <input
                      className="input"
                      value={company.phone}
                      onChange={(e) => setCompany({ ...company, phone: e.target.value })}
                    />
                  </Field>
                  <Field label="Business email">
                    <input
                      className="input"
                      type="email"
                      value={company.email}
                      onChange={(e) => setCompany({ ...company, email: e.target.value })}
                    />
                  </Field>
                </div>
                <Field label="Business address">
                  <textarea
                    className="input"
                    rows={2}
                    value={company.address}
                    onChange={(e) => setCompany({ ...company, address: e.target.value })}
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label="Gas Safe Registration No."
                    hint="The 1–6 digit number issued to your business."
                  >
                    <input
                      className="input font-mono"
                      inputMode="numeric"
                      maxLength={6}
                      value={company.gasRegistrationNumber}
                      onChange={(e) =>
                        setCompany({
                          ...company,
                          gasRegistrationNumber: e.target.value.replace(/\D/g, '').slice(0, 6),
                        })
                      }
                    />
                  </Field>
                  <Field label="VAT rate %" hint="Default 20% — only paid once VAT registered.">
                    <input
                      className="input"
                      type="number"
                      min={0}
                      max={50}
                      value={company.vatRate}
                      onChange={(e) =>
                        setCompany({ ...company, vatRate: Number(e.target.value) })
                      }
                    />
                  </Field>
                </div>
              </div>
              <PanelActions>
                <button className="btn-secondary" onClick={() => go(-1)}>Back</button>
                <button
                  className="btn-primary"
                  disabled={submitting}
                  onClick={saveCompany}
                >
                  {submitting ? 'Saving…' : 'Continue'}
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              </PanelActions>
            </Panel>
          )}

          {stepKey === 'profile' && (
            <Panel
              title="Your profile"
              subtitle="Your Gas Safe ID appears on every certificate you produce. You can update it any time from your profile."
            >
              <div className="space-y-4">
                <Field label="Your name">
                  <input
                    className="input"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Phone">
                    <input
                      className="input"
                      value={profile.phone}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    />
                  </Field>
                  <Field
                    label="Gas Safe Licence No."
                    hint="The 7-digit number on your ID card."
                  >
                    <input
                      className="input font-mono"
                      inputMode="numeric"
                      maxLength={7}
                      value={profile.gasSafeNumber}
                      onChange={(e) =>
                        setProfile({
                          ...profile,
                          gasSafeNumber: e.target.value.replace(/\D/g, '').slice(0, 7),
                        })
                      }
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Base postcode" hint="Where your day starts from.">
                    <input
                      className="input"
                      value={profile.basePostcode}
                      onChange={(e) =>
                        setProfile({ ...profile, basePostcode: e.target.value.toUpperCase() })
                      }
                    />
                  </Field>
                  <Field label="Service radius (miles)">
                    <input
                      className="input"
                      type="number"
                      min={0}
                      max={300}
                      value={profile.serviceRadiusMiles}
                      onChange={(e) =>
                        setProfile({ ...profile, serviceRadiusMiles: Number(e.target.value) })
                      }
                    />
                  </Field>
                </div>
                <QualificationChips
                  value={profile.qualifications}
                  onChange={(q) => setProfile({ ...profile, qualifications: q })}
                />
              </div>
              <PanelActions>
                <button className="btn-secondary" onClick={() => go(-1)}>Back</button>
                <button
                  className="btn-primary"
                  disabled={submitting}
                  onClick={saveProfile}
                >
                  {submitting ? 'Saving…' : 'Continue'}
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              </PanelActions>
            </Panel>
          )}

          {stepKey === 'policy' && isCompanyPlan && (
            <Panel
              title="Quote policy"
              subtitle="Decide how engineers in the field are allowed to issue quotes on your behalf."
            >
              <div className="space-y-3">
                <PolicyOption
                  selected={policy.model === 'THRESHOLD'}
                  onSelect={() => setPolicy({ ...policy, model: 'THRESHOLD' })}
                  title="Threshold approval"
                  description="Engineers submit quotes. Anything over the threshold needs admin sign-off; smaller jobs auto-send."
                />
                <PolicyOption
                  selected={policy.model === 'ALWAYS_ADMIN'}
                  onSelect={() => setPolicy({ ...policy, model: 'ALWAYS_ADMIN' })}
                  title="Always admin approval"
                  description="Every field quote is reviewed by an admin before going to the customer."
                />
                <PolicyOption
                  selected={policy.model === 'TRUST'}
                  onSelect={() => setPolicy({ ...policy, model: 'TRUST' })}
                  title="Trust engineers"
                  description="Engineers can send any quote instantly, within discount limits."
                />

                {policy.model === 'THRESHOLD' && (
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <Field label="Threshold amount (£)">
                      <input
                        type="number"
                        min={0}
                        className="input"
                        value={policy.thresholdAmount}
                        onChange={(e) =>
                          setPolicy({ ...policy, thresholdAmount: Number(e.target.value) })
                        }
                      />
                    </Field>
                    <Field label="Max discount %">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        className="input"
                        value={policy.maxDiscountPct}
                        onChange={(e) =>
                          setPolicy({ ...policy, maxDiscountPct: Number(e.target.value) })
                        }
                      />
                    </Field>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 pt-2">
                  <Field label="Validity (days)">
                    <input
                      type="number"
                      min={1}
                      max={90}
                      className="input"
                      value={policy.validDays}
                      onChange={(e) => setPolicy({ ...policy, validDays: Number(e.target.value) })}
                    />
                  </Field>
                </div>

                <div className="space-y-2 pt-2">
                  <Toggle
                    label="Require customer signature on the field"
                    value={policy.requireCustomerSignature}
                    onChange={(v) => setPolicy({ ...policy, requireCustomerSignature: v })}
                  />
                  <Toggle
                    label="Allow engineers to add custom (non-price-book) items"
                    value={policy.allowCustomItems}
                    onChange={(v) => setPolicy({ ...policy, allowCustomItems: v })}
                  />
                  <Toggle
                    label="Notify admins when a quote is submitted"
                    value={policy.notifyAdminsOnSubmit}
                    onChange={(v) => setPolicy({ ...policy, notifyAdminsOnSubmit: v })}
                  />
                  <Toggle
                    label="Auto-convert accepted quotes into jobs"
                    value={policy.autoConvertOnAcceptance}
                    onChange={(v) => setPolicy({ ...policy, autoConvertOnAcceptance: v })}
                  />
                </div>
              </div>
              <PanelActions>
                <button className="btn-secondary" onClick={() => go(-1)}>Back</button>
                <button
                  className="btn-primary"
                  disabled={submitting}
                  onClick={savePolicy}
                >
                  {submitting ? 'Saving…' : 'Continue'}
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              </PanelActions>
            </Panel>
          )}

          {stepKey === 'invite' && isCompanyPlan && (
            <Panel
              title="Invite your team"
              subtitle={`You have ${statusData?.seats ?? '–'} paid seats. Send them an invite now — you can also add people later from Settings.`}
            >
              <div className="space-y-3">
                {invites.map((value, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input
                      type="email"
                      className="input flex-1"
                      placeholder="engineer@yourcompany.co.uk"
                      value={value}
                      onChange={(e) => {
                        const next = [...invites];
                        next[idx] = e.target.value;
                        setInvites(next);
                      }}
                    />
                    <button
                      className="btn-secondary"
                      onClick={() => setInvites(invites.filter((_, i) => i !== idx))}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="text-sm text-orange-600 font-medium"
                  onClick={() => setInvites([...invites, ''])}
                >
                  + Add another
                </button>
              </div>
              <PanelActions>
                <button className="btn-secondary" onClick={() => go(-1)}>Back</button>
                <button className="btn-secondary" onClick={() => go(1)}>Skip for now</button>
                <button
                  className="btn-primary"
                  disabled={submitting}
                  onClick={sendInvites}
                >
                  {submitting ? 'Sending…' : 'Send invites'}
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              </PanelActions>
            </Panel>
          )}

          {stepKey === 'done' && (
            <Panel
              title="You're all set"
              subtitle="Your workspace is ready. You can come back to any of these screens from Settings."
            >
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 flex items-start gap-3">
                <CheckCircleIcon className="w-6 h-6 text-emerald-600 mt-0.5" />
                <div className="text-sm text-emerald-900">
                  Company details saved. Gas Safe info captured. {isCompanyPlan ? 'Quote policy configured.' : 'Simple quoting enabled.'}{' '}
                  {isCompanyPlan ? 'Team invites are on their way.' : ''}
                </div>
              </div>
              <PanelActions>
                <button className="btn-primary" disabled={submitting} onClick={finish}>
                  {submitting ? 'Finishing…' : 'Open dashboard'}
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              </PanelActions>
            </Panel>
          )}
        </div>
      </main>

      <style>{`
        .input {
          width: 100%; padding: 10px 12px; border-radius: 10px;
          border: 1px solid #e2e8f0; background: #fff; font-size: 14px;
        }
        .input:focus { outline: none; border-color: #FF7A1A; box-shadow: 0 0 0 3px rgba(255,122,26,0.15); }
        .btn-primary {
          display: inline-flex; align-items: center; gap: 6px;
          background: #0f172a; color: #fff; padding: 10px 16px; border-radius: 10px;
          font-weight: 600; font-size: 14px;
        }
        .btn-primary:disabled { opacity: .7; cursor: not-allowed; }
        .btn-secondary {
          display: inline-flex; align-items: center; gap: 6px;
          background: #f1f5f9; color: #0f172a; padding: 10px 16px; border-radius: 10px;
          font-weight: 600; font-size: 14px;
        }
      `}</style>
    </div>
  );
}

// ---------------- helpers ----------------

const Panel: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({
  title,
  subtitle,
  children,
}) => (
  <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
    <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
    {subtitle && <p className="text-slate-600 text-sm mt-1">{subtitle}</p>}
    <div className="mt-6">{children}</div>
  </div>
);

const PanelActions: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mt-8 flex items-center justify-end gap-2">{children}</div>
);

const PanelBullet: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="flex items-start gap-2">
    <CheckCircleIcon className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
    {children}
  </li>
);

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({
  label,
  hint,
  children,
}) => (
  <label className="block">
    <span className="text-xs font-medium text-slate-700 mb-1 block">{label}</span>
    {children}
    {hint && <span className="text-xs text-slate-500 mt-1 block">{hint}</span>}
  </label>
);

const Toggle: React.FC<{ label: string; value: boolean; onChange: (v: boolean) => void }> = ({
  label,
  value,
  onChange,
}) => (
  <label className="flex items-center gap-3 cursor-pointer">
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`relative w-10 h-5 rounded-full transition-colors ${
        value ? 'bg-emerald-500' : 'bg-slate-300'
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
          value ? 'translate-x-5' : ''
        }`}
      />
    </button>
    <span className="text-sm text-slate-700">{label}</span>
  </label>
);

const PolicyOption: React.FC<{
  selected: boolean;
  onSelect: () => void;
  title: string;
  description: string;
}> = ({ selected, onSelect, title, description }) => (
  <button
    type="button"
    onClick={onSelect}
    className={`w-full text-left p-4 rounded-xl border transition ${
      selected
        ? 'border-orange-500 bg-orange-50/70 ring-2 ring-orange-100'
        : 'border-slate-200 hover:border-slate-300'
    }`}
  >
    <div className="font-medium text-sm text-slate-900">{title}</div>
    <div className="text-xs text-slate-600 mt-1">{description}</div>
  </button>
);

const QualificationChips: React.FC<{
  value: string[];
  onChange: (next: string[]) => void;
}> = ({ value, onChange }) => {
  const preset = ['CCN1', 'CENWAT', 'CKR1', 'HTR1', 'MET1', 'CPA1', 'ACS commercial'];
  const toggle = (item: string) =>
    onChange(value.includes(item) ? value.filter((v) => v !== item) : [...value, item]);
  return (
    <div>
      <span className="text-xs font-medium text-slate-700 mb-2 block">Qualifications</span>
      <div className="flex flex-wrap gap-2">
        {preset.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => toggle(p)}
            className={`px-3 py-1.5 text-xs rounded-full border ${
              value.includes(p)
                ? 'border-orange-500 bg-orange-50 text-orange-700'
                : 'border-slate-200 text-slate-600 hover:border-slate-300'
            }`}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
};
